import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Select from "react-select";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
    Card,
    CardBody,
    Row,
    Col,
    Form,
    Input,
    Button,
    Label,
    FormGroup,
    Container,
} from "reactstrap";
import Loaders from "components/Common/Loaders";
import { urlEndPoint } from "helpers/GeneralHelper";
import { registerBase64UploadAdapter } from "helpers/ckeditorBase64UploadAdapter";
import { registerMailTemplateSourceToolbarButton } from "helpers/ckeditorMailSourceButton";
import { enableEditableMailPreview } from "helpers/mailTemplatePreviewFrame";
import {
    addMailTemplate,
    getMailTemplateById,
    getMailTemplateDefinitions,
    updateMailTemplate,
} from "model/MailTemplateModel";

const initialFormState = {
    name: "",
    note: "",
    subject: "",
    body: "",
    filterSyntax: "",
    filterData: "",
    statusId: "",
    isActive: true,
};

const formatDataTag = (propertyName = "") => `[{code:'${propertyName}'}]`;

const ACTIVE_FIELD_LABELS = {
    note: "Note",
    subject: "Subject",
    body: "Body",
    filterSyntax: "Filter Syntax",
    filterData: "Filter Value",
};

const resolveTemplateItemName = (item = {}) =>
    String(item?.name ?? item?.Name ?? item?.templateName ?? "").trim();

const mapDetailToFormState = (detail = {}, resolvedTemplateName = "") => {
    const statusValue = String(detail?.statusId ?? detail?.status ?? "").toLowerCase();

    return {
        name: resolvedTemplateName,
        note: detail?.note ?? "",
        subject: detail?.subject ?? "",
        body: detail?.body ?? detail?.Body ?? "",
        filterSyntax: detail?.filterSyntax ?? "",
        filterData: detail?.filterData ?? detail?.filter_data ?? "",
        statusId: detail?.statusId ?? "",
        isActive: statusValue === "active" || statusValue === "1" || statusValue === "true",
    };
};

const isEmailLayoutHtml = (html = "") => {
    const value = String(html || "");
    return (
        /<table[\s>]/i.test(value) ||
        /bgcolor\s*=/i.test(value) ||
        /cellspacing\s*=/i.test(value) ||
        /cellpadding\s*=/i.test(value)
    );
};

const buildEmailPreviewHtml = (body = "") => {
    const html = String(body || "").trim();
    if (!html) return "";

    if (/<html[\s>]/i.test(html)) {
        return html;
    }

    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<link href="https://fonts.googleapis.com/css?family=Rubik:300,400,500,700|Open+Sans:300,400,600,700" rel="stylesheet" />
</head>
<body style="margin:0;padding:0;background:#f2f3f8;">
${html}
</body>
</html>`;
};

const findDefinitionByTemplateName = (definitionList = [], templateName = "") => {
    const normalizedTemplateName = String(templateName || "").trim().toLowerCase();
    if (!normalizedTemplateName) return null;

    return (
        definitionList.find(
            (definition) =>
                String(definition?.name || "").trim().toLowerCase() === normalizedTemplateName
        ) || null
    );
};

const AddEditMailTemplate = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id: templateId } = useParams();
    const isEditMode = Boolean(templateId);

    const [formData, setFormData] = useState(initialFormState);
    const [templateItemName, setTemplateItemName] = useState("");
    const [selectedTemplateKey, setSelectedTemplateKey] = useState("");
    const [definitions, setDefinitions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeField, setActiveField] = useState("body");
    const [editorKey, setEditorKey] = useState(0);
    const [statusFormKey, setStatusFormKey] = useState(0);
    const [bodyEditorMode, setBodyEditorMode] = useState("visual");
    const editorRef = useRef(null);
    const previewFrameRef = useRef(null);
    const previewCleanupRef = useRef(null);
    const formDataRef = useRef(formData);
    const bodyEditorModeRef = useRef(bodyEditorMode);
    const onSourceToggleRef = useRef(() => {});
    const [previewSeed, setPreviewSeed] = useState("");
    const [previewRevision, setPreviewRevision] = useState(0);

    useEffect(() => {
        formDataRef.current = formData;
    }, [formData]);

    useEffect(() => {
        bodyEditorModeRef.current = bodyEditorMode;
    }, [bodyEditorMode]);

    const syncPreviewHtmlToBody = useCallback((html = "") => {
        const nextHtml = String(html ?? "");
        setFormData((previous) => {
            if (previous.body === nextHtml) return previous;
            return { ...previous, body: nextHtml };
        });
    }, []);

    const readPreviewHtmlFromFrame = useCallback(() => {
        const doc = previewFrameRef.current?.contentDocument;
        if (!doc?.body) return formDataRef.current?.body || "";
        return doc.body.innerHTML;
    }, []);

    const refreshVisualPreview = useCallback((html = "") => {
        const nextHtml = String(html || formDataRef.current?.body || "");
        setPreviewSeed(nextHtml);
        setPreviewRevision((revision) => revision + 1);
    }, []);

    onSourceToggleRef.current = (enteringSource, editor) => {
        if (enteringSource) {
            if (String(formDataRef.current?.body || "").trim()) {
                syncPreviewHtmlToBody(readPreviewHtmlFromFrame());
            }
            setBodyEditorMode("source");
            return;
        }

        const html = formDataRef.current?.body || "";
        editor.setData(isEmailLayoutHtml(html) ? "" : html);
        refreshVisualPreview(html);
        setBodyEditorMode("visual");
    };

    useEffect(() => {
        const button = document.querySelector(
            ".mail-template-editor-shell [data-mail-source-btn]"
        );
        if (!button) return;

        if (bodyEditorMode === "source") {
            button.classList.add("ck-on");
            button.classList.remove("ck-off");
            return;
        }

        button.classList.add("ck-off");
        button.classList.remove("ck-on");
    }, [bodyEditorMode, editorKey]);

    const matchedDefinition = useMemo(
        () => findDefinitionByTemplateName(definitions, templateItemName),
        [definitions, templateItemName]
    );

    const dataTags = matchedDefinition?.properties || [];

    const templateSelectOptions = useMemo(
        () =>
            definitions
                .filter((definition) => definition?.enable !== false)
                .map((definition) => {
                    const optionName = resolveTemplateItemName(definition);
                    return {
                        value: optionName,
                        label: definition?.description
                            ? `${optionName} - ${definition.description}`
                            : optionName,
                    };
                }),
        [definitions]
    );

    const getSelectedOption = (options, value) =>
        options.find((option) => String(option?.value) === String(value)) || null;

    useEffect(() => {
        const bootstrap = async () => {
            setLoading(true);
            try {
                const definitionsResponse = await getMailTemplateDefinitions({});
                const definitionList = definitionsResponse?.data || [];
                const normalizedDefinitions = Array.isArray(definitionList) ? definitionList : [];
                setDefinitions(normalizedDefinitions);

                const nameFromNavigation = resolveTemplateItemName({
                    name: location?.state?.templateName,
                });

                if (templateId) {
                    const detailResponse = await getMailTemplateById(templateId);
                    const detail = detailResponse?.data || {};
                    const resolvedTemplateName =
                        resolveTemplateItemName(detail) || nameFromNavigation;

                    setTemplateItemName(resolvedTemplateName);
                    setSelectedTemplateKey(resolvedTemplateName);
                    const mappedForm = mapDetailToFormState(detail, resolvedTemplateName);
                    setFormData(mappedForm);
                    setPreviewSeed(mappedForm.body || "");
                    setBodyEditorMode("visual");
                    setEditorKey((key) => key + 1);
                    setStatusFormKey((key) => key + 1);
                } else if (nameFromNavigation) {
                    setTemplateItemName(nameFromNavigation);
                    setSelectedTemplateKey(nameFromNavigation);
                    setFormData((previous) => ({
                        ...previous,
                        name: nameFromNavigation,
                    }));
                }
            } catch (error) {
                toast.error(error?.message || "Unable to load mail template");
            } finally {
                setLoading(false);
            }
        };

        bootstrap();
    }, [templateId, location?.state?.templateName]);

    const applySelectedTemplate = (selectedDefinition) => {
        if (!selectedDefinition) {
            setTemplateItemName("");
            setFormData(initialFormState);
            resetBodyEditor();
            setStatusFormKey((key) => key + 1);
            return;
        }

        const resolvedTemplateName = resolveTemplateItemName(selectedDefinition);
        setTemplateItemName(resolvedTemplateName);
        setFormData({
            ...initialFormState,
            name: resolvedTemplateName,
        });
        resetBodyEditor();
        setStatusFormKey((key) => key + 1);
    };

    const handleTemplateSelect = (selectedOption) => {
        const nextKey = selectedOption?.value || "";
        setSelectedTemplateKey(nextKey);

        if (!nextKey) {
            applySelectedTemplate(null);
            return;
        }

        const selectedDefinition = findDefinitionByTemplateName(definitions, nextKey);
        applySelectedTemplate(selectedDefinition);
    };

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setFormData((previous) => ({
            ...previous,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const insertIntoTextField = (fieldName, tagText) => {
        setFormData((previous) => ({
            ...previous,
            [fieldName]: `${previous?.[fieldName] || ""}${tagText}`,
        }));
    };

    const resetBodyEditor = () => {
        setBodyEditorMode("visual");
        setPreviewSeed("");
        setPreviewRevision((revision) => revision + 1);
        setEditorKey((key) => key + 1);
    };

    const hasBodyContent = String(formData.body || "").trim().length > 0;
    const showEditablePreview = bodyEditorMode !== "source" && hasBodyContent;

    const handlePreviewFrameLoad = useCallback(() => {
        if (previewCleanupRef.current) {
            previewCleanupRef.current();
            previewCleanupRef.current = null;
        }

        previewCleanupRef.current = enableEditableMailPreview(
            previewFrameRef.current,
            syncPreviewHtmlToBody
        );
    }, [syncPreviewHtmlToBody]);

    useEffect(
        () => () => {
            if (previewCleanupRef.current) {
                previewCleanupRef.current();
            }
        },
        []
    );

    const handleBodySourceChange = (event) => {
        const { value } = event.target;
        setFormData((previous) => ({
            ...previous,
            body: value,
        }));
    };

    const insertTag = (propertyName) => {
        const tagText = formatDataTag(propertyName);

        if (activeField === "body" && bodyEditorMode === "source") {
            insertIntoTextField("body", tagText);
            return;
        }

        if (activeField === "body" && bodyEditorMode === "visual" && hasBodyContent) {
            const nextBody = `${formDataRef.current?.body || ""}${tagText}`;
            syncPreviewHtmlToBody(nextBody);
            refreshVisualPreview(nextBody);
            return;
        }

        if (activeField === "body" && editorRef.current) {
            const editor = editorRef.current;
            editor.model.change((writer) => {
                const insertPosition = editor.model.document.selection.getFirstPosition();
                writer.insertText(tagText, insertPosition);
            });
            setFormData((previous) => ({
                ...previous,
                body: editor.getData(),
            }));
            return;
        }

        if (["note", "subject", "filterSyntax", "filterData"].includes(activeField)) {
            insertIntoTextField(activeField, tagText);
            return;
        }

        insertIntoTextField("body", tagText);
    };

    const resolveBodyForSubmit = () => {
        if (bodyEditorModeRef.current === "visual" && String(formDataRef.current?.body || "").trim()) {
            return readPreviewHtmlFromFrame();
        }
        if (bodyEditorModeRef.current === "visual" && editorRef.current) {
            return editorRef.current.getData() || formDataRef.current?.body || "";
        }
        return formDataRef.current?.body || "";
    };

    const buildSubmitPayload = () => ({
        name: String(formData?.name || templateItemName || "").trim(),
        note: String(formData?.note || "").trim(),
        subject: String(formData?.subject || "").trim(),
        body: resolveBodyForSubmit(),
        filterSyntax: String(formData?.filterSyntax || "").trim(),
        filterData: String(formData?.filterData || "").trim(),
        statusId: formData?.isActive ? "active" : "deactive",
    });

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (bodyEditorModeRef.current === "visual" && String(formDataRef.current?.body || "").trim()) {
            syncPreviewHtmlToBody(readPreviewHtmlFromFrame());
        }
        setSaving(true);
        try {
            const payload = buildSubmitPayload();
            const response = isEditMode
                ? await updateMailTemplate(templateId, payload)
                : await addMailTemplate(payload);

            if (response?.success) {
                toast.success(response?.message || "Mail template saved successfully");
                navigate(`/${urlEndPoint.mailTemplate}`);
            } else {
                toast.error(response?.message || "Unable to save mail template");
            }
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="page-content">
                <Container fluid>
                    <Loaders />
                </Container>
            </div>
        );
    }

    const activeFieldLabel = ACTIVE_FIELD_LABELS[activeField] || "Body";

    return (
        <div className="page-content mail-template-editor-page">
            <Container fluid>
                <Card className="border-0 shadow-sm">
                    <CardBody className="p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <h5 className="fw-bold mb-1">
                                    {isEditMode ? "Edit Mail Template" : "Add Mail Template"}
                                </h5>
                                {templateItemName ? (
                                    <strong className="text-muted mb-0">{templateItemName}</strong>
                                ) : null}
                                {/* {matchedDefinition?.description ? (
                                    <p className="text-muted mb-0 small">{matchedDefinition.description}</p>
                                ) : null} */}
                            </div>
                            <Button
                                color="light"
                                className="border"
                                type="button"
                                onClick={() => navigate(`/${urlEndPoint.mailTemplate}`)}
                            >
                                Back
                            </Button>
                        </div>
                        <hr />

                        <Form onSubmit={handleSubmit}>
                            <Row className="g-4">
                                <Col lg={8}>
                                    {!isEditMode ? (
                                        <Row>
                                            <Col md={12}>
                                                <FormGroup>
                                                    <Label>Select Template</Label>
                                                    <Select
                                                        options={templateSelectOptions}
                                                        className="select2-selection"
                                                        placeholder="Select Template"
                                                        value={getSelectedOption(
                                                            templateSelectOptions,
                                                            selectedTemplateKey
                                                        )}
                                                        onChange={handleTemplateSelect}
                                                        isClearable
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                    ) : null}

                                    <Row>
                                        <Col md={12}>
                                            <FormGroup>
                                                <Label>Note</Label>
                                                <Input
                                                    type="text"
                                                    name="note"
                                                    value={formData.note}
                                                    onChange={handleChange}
                                                    onFocus={() => setActiveField("note")}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Filter Syntax</Label>
                                                <Input
                                                    type="text"
                                                    name="filterSyntax"
                                                    value={formData.filterSyntax}
                                                    onChange={handleChange}
                                                    onFocus={() => setActiveField("filterSyntax")}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Filter Value</Label>
                                                <Input
                                                    type="text"
                                                    name="filterData"
                                                    value={formData.filterData}
                                                    onChange={handleChange}
                                                    onFocus={() => setActiveField("filterData")}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={12}>
                                            <FormGroup>
                                                <Label>Subject</Label>
                                                <Input
                                                    type="text"
                                                    name="subject"
                                                    value={formData.subject}
                                                    onChange={handleChange}
                                                    onFocus={() => setActiveField("subject")}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={12}>
                                            <FormGroup>
                                                <Label>Body</Label>
                                                {/* <small className="text-muted d-block mb-2">
                                                    Paste table-based email HTML in Source (&lt;&gt;) mode. Layout
                                                    preview appears below while you edit.
                                                </small> */}
                                                <div
                                                    className={`mail-template-editor-shell border rounded${
                                                        bodyEditorMode === "source" ? " is-source-mode" : ""
                                                    }${showEditablePreview ? " has-email-preview" : ""}`}
                                                    onFocus={() => setActiveField("body")}
                                                >
                                                    <CKEditor
                                                        key={editorKey}
                                                        editor={ClassicEditor}
                                                        data={showEditablePreview ? "" : formData.body || ""}
                                                        onReady={(editor) => {
                                                            editorRef.current = editor;
                                                            registerBase64UploadAdapter(editor);
                                                            registerMailTemplateSourceToolbarButton(
                                                                editor,
                                                                (enteringSource, activeEditor) => {
                                                                    onSourceToggleRef.current(
                                                                        enteringSource,
                                                                        activeEditor
                                                                    );
                                                                },
                                                                bodyEditorModeRef.current === "source"
                                                            );
                                                        }}
                                                        onChange={(event, editor) => {
                                                            if (bodyEditorModeRef.current === "source") return;
                                                            if (showEditablePreview) return;
                                                            const nextBody = editor.getData();
                                                            setFormData((previous) => ({
                                                                ...previous,
                                                                body: nextBody,
                                                            }));
                                                            if (String(nextBody || "").trim()) {
                                                                setPreviewSeed(nextBody);
                                                            }
                                                        }}
                                                    />
                                                    {bodyEditorMode === "source" ? (
                                                        <textarea
                                                            className="mail-template-source-textarea form-control"
                                                            value={formData.body}
                                                            onChange={handleBodySourceChange}
                                                            onFocus={() => setActiveField("body")}
                                                            spellCheck={false}
                                                        />
                                                    ) : null}
                                                    {showEditablePreview ? (
                                                        <div className="mail-template-email-preview">
                                                            <iframe
                                                                key={`mail-preview-${editorKey}-${previewRevision}`}
                                                                ref={previewFrameRef}
                                                                title="Mail template preview"
                                                                className="mail-template-email-preview__frame"
                                                                srcDoc={buildEmailPreviewHtml(
                                                                    previewSeed || formData.body
                                                                )}
                                                                sandbox="allow-same-origin"
                                                                onLoad={handlePreviewFrameLoad}
                                                            />
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={12}>
                                            <FormGroup key={`mail-template-status-${statusFormKey}`}>
                                                <Label>Status</Label>
                                                <div className="input-box d-flex align-items-center justify-content-between">
                                                    <span className="text-muted">Choose Status</span>
                                                    <div>
                                                        <Input
                                                            type="radio"
                                                            name="isActive"
                                                            defaultChecked={formData.isActive}
                                                            onChange={() =>
                                                                setFormData((previous) => ({
                                                                    ...previous,
                                                                    isActive: true,
                                                                }))
                                                            }
                                                        />{" "}
                                                        Active
                                                        <Input
                                                            type="radio"
                                                            name="isActive"
                                                            className="ms-3"
                                                            defaultChecked={!formData.isActive}
                                                            onChange={() =>
                                                                setFormData((previous) => ({
                                                                    ...previous,
                                                                    isActive: false,
                                                                }))
                                                            }
                                                        />{" "}
                                                        Deactive
                                                    </div>
                                                </div>
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <div className="text-end mt-3">
                                        <Button color="primary" type="submit" disabled={saving}>
                                            {isEditMode ? "Update" : "Save"}
                                        </Button>
                                    </div>
                                </Col>

                                <Col lg={4}>
                                    <Card className="mail-template-tag-panel border-0 shadow-sm h-100">
                                        <CardBody className="mail-template-tag-panel__body">
                                            <div className="mail-template-tag-panel__header">
                                                <div>
                                                    <h6 className="mail-template-tag-panel__title mb-0">
                                                        <i className="fa fa-tag me-2" aria-hidden="true" />
                                                        Data Tags
                                                    </h6>
                                                    <p className="mail-template-tag-panel__hint mb-0">
                                                        Double-click a tag to insert it.
                                                    </p>
                                                </div>
                                                <span className="mail-template-tag-panel__count">
                                                    {dataTags.length}
                                                </span>
                                            </div>

                                            <div className="mail-template-active-field">
                                                <span className="mail-template-active-field__label">
                                                    Inserting into
                                                </span>
                                                <span className="mail-template-active-field__value">
                                                    {activeFieldLabel}
                                                </span>
                                            </div>

                                            {!templateItemName ? (
                                                <div className="mail-template-tag-empty">
                                                    Select a template to load data tags.
                                                </div>
                                            ) : !dataTags.length ? (
                                                <div className="mail-template-tag-empty">
                                                    No data tags found for{" "}
                                                    <strong>{templateItemName}</strong>.
                                                </div>
                                            ) : (
                                                <div className="mail-template-tag-list">
                                                    {dataTags.map((propertyName) => (
                                                        <button
                                                            key={propertyName}
                                                            type="button"
                                                            className="mail-template-tag-chip"
                                                            onDoubleClick={() => insertTag(propertyName)}
                                                            title={`Insert ${formatDataTag(propertyName)}`}
                                                        >
                                                            <span className="mail-template-tag-chip__label">
                                                                {propertyName}
                                                            </span>
                                                            <span className="mail-template-tag-chip__code">
                                                                {formatDataTag(propertyName)}
                                                            </span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>
                        </Form>
                    </CardBody>
                </Card>
            </Container>

            <style>{`
                .mail-template-editor-page .mail-template-editor-shell {
                    min-height: 360px;
                    overflow: hidden;
                    background: #fff;
                }
                .mail-template-editor-page .mail-template-source-toolbar-btn {
                    min-width: 34px;
                    padding-left: 6px !important;
                    padding-right: 6px !important;
                }
                .mail-template-editor-page .mail-template-source-glyph {
                    display: inline-flex !important;
                    align-items: center;
                    justify-content: center;
                    min-width: 20px;
                    min-height: 20px;
                    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace !important;
                    font-size: 15px !important;
                    font-weight: 700 !important;
                    line-height: 1 !important;
                    letter-spacing: -0.02em;
                    color: var(--ck-color-text, #333) !important;
                }
                .mail-template-editor-page .mail-template-source-textarea {
                    display: block;
                    width: 100%;
                    min-height: 480px;
                    resize: vertical;
                    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
                    font-size: 13px;
                    line-height: 1.5;
                    border: 0;
                    border-top: 1px solid #e7e9ef;
                    border-radius: 0;
                    box-shadow: none;
                }
                .mail-template-editor-page .mail-template-editor-shell.is-source-mode .ck-editor__main,
                .mail-template-editor-page .mail-template-editor-shell.has-email-preview .ck-editor__main {
                    display: none !important;
                }
                .mail-template-editor-page .mail-template-editor-shell.is-source-mode .mail-template-email-preview {
                    display: none !important;
                }
                .mail-template-editor-page .mail-template-editor-shell .ck-editor__editable {
                    min-height: 320px;
                }
                .mail-template-editor-page .mail-template-email-preview {
                    border-top: 1px solid #e7e9ef;
                    background: #f8fafc;
                }
                .mail-template-editor-page .mail-template-email-preview__label {
                    padding: 8px 12px;
                    font-size: 12px;
                    font-weight: 600;
                    color: #6c757d;
                    text-transform: uppercase;
                    letter-spacing: 0.04em;
                }
                .mail-template-editor-page .mail-template-email-preview__frame {
                    display: block;
                    width: 100%;
                    height: 520px;
                    border: 0;
                    background: #f2f3f8;
                    cursor: text;
                }
                .mail-template-editor-page .ck-content img,
                .mail-template-editor-page .ck-editor__editable img {
                    max-width: 100%;
                    height: auto;
                }
            `}</style>
        </div>
    );
};

export default AddEditMailTemplate;
